import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  AppContainerOptions,
  ContentSideNavButton,
  LinkSideNavButton,
  RightDrawerButton,
  RightDrawerContent,
  SideNavButton,
  SideNavButtonAlignment
} from '@mydocma/app-container';
import { Project, ProjectService } from '@mydocma/project';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { MessageCenterComponent } from '../common/components/message-center/message-center.component';
import { PRRoutesEnum } from '../routes/pr-route.enums';

export enum SideNavBarButtonsEnum {
  DASHBOARD = 'dashboard',
  PROJECT_PARTICIPANTS = 'project_participants',
  TASKS = 'tasks',
  INBOX = 'inbox',
  DOCUMENTS = 'documents',
  BIM_MODELS = 'bim_models',
  PLANS = 'plans',
  PICTURES = 'pictures',
  MESSAGES = 'messages',
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class SideNavbarService {
  buttons: SideNavButton[] = [];
  appContainerOptions: AppContainerOptions | null = null;
  currentProject?: Project;

  private readonly idToButtonMap = new Map<string, SideNavButton>([
    [
      SideNavBarButtonsEnum.DASHBOARD,
      new LinkSideNavButton({
        id: SideNavBarButtonsEnum.DASHBOARD,
        icon: 'insert_chart_outlined',
      }),
    ],
    [
      SideNavBarButtonsEnum.DOCUMENTS,
      new LinkSideNavButton({
        id: SideNavBarButtonsEnum.DOCUMENTS,
        icon: 'view_list',
      }),
    ],
    [
      SideNavBarButtonsEnum.PROJECT_PARTICIPANTS,
      new LinkSideNavButton({
        id: SideNavBarButtonsEnum.PROJECT_PARTICIPANTS,
        icon: '_user-account',
        disabled: true,
      }),
    ],
    [
      SideNavBarButtonsEnum.TASKS,
      new LinkSideNavButton({
        id: SideNavBarButtonsEnum.TASKS,
        icon: '_task',
        disabled: true,
      }),
    ],
    [
      SideNavBarButtonsEnum.INBOX,
      new LinkSideNavButton({
        id: SideNavBarButtonsEnum.INBOX,
        icon: '_inbox',
        disabled: true,
      }),
    ],
    [
      SideNavBarButtonsEnum.BIM_MODELS,
      new LinkSideNavButton({
        id: SideNavBarButtonsEnum.BIM_MODELS,
        icon: '_bim-model',
        disabled: true,
      }),
    ],
    [
      SideNavBarButtonsEnum.PLANS,
      new LinkSideNavButton({
        id: SideNavBarButtonsEnum.PLANS,
        icon: '_plan_without_pin',
        disabled: true,
      }),
    ],
    [
      SideNavBarButtonsEnum.PICTURES,
      new LinkSideNavButton({
        id: SideNavBarButtonsEnum.PICTURES,
        icon: 'image',
        disabled: true,
      }),
    ],
    [
      SideNavBarButtonsEnum.MESSAGES,
      new RightDrawerButton({
        id: SideNavBarButtonsEnum.MESSAGES,
        icon: '_megaphone_outline',
        alignment: SideNavButtonAlignment.bottom,
        contentId: SideNavBarButtonsEnum.MESSAGES,
      }),
    ],
  ]);

  private readonly idToUrlMap = new Map<string, string>([
    [SideNavBarButtonsEnum.DASHBOARD, PRRoutesEnum.DASHBOARD_WITH_PROJECT_ID],
    [SideNavBarButtonsEnum.DOCUMENTS, PRRoutesEnum.DOCUMENTS_WITH_PROJECT_ID],
    [SideNavBarButtonsEnum.INBOX, PRRoutesEnum.INBOX_WITH_PROJECT_ID],
  ]);

  private readonly idToTranslationKey = new Map<string, string>([
    [SideNavBarButtonsEnum.DASHBOARD, 'PR.sidebar.dashboard'],
    [SideNavBarButtonsEnum.PROJECT_PARTICIPANTS, 'PR.sidebar.project_participants'],
    [SideNavBarButtonsEnum.TASKS, 'PR.sidebar.tasks'],
    [SideNavBarButtonsEnum.INBOX, 'PR.sidebar.inbox'],
    [SideNavBarButtonsEnum.DOCUMENTS, 'PR.sidebar.documents'],
    [SideNavBarButtonsEnum.BIM_MODELS, 'PR.sidebar.bim_models'],
    [SideNavBarButtonsEnum.PLANS, 'PR.sidebar.plans'],
    [SideNavBarButtonsEnum.PICTURES, 'PR.sidebar.pictures'],
    [SideNavBarButtonsEnum.MESSAGES, 'PR.sidebar.messages'],
  ]);

  constructor(
    private readonly translateService: TranslateService,
    private readonly route: Router,
    private readonly projectService: ProjectService,
  ) {
    this.projectService.currentProject.pipe(untilDestroyed(this)).subscribe((project) => {
      if (project) {
        this.currentProject = project;
        this.initButtons();
      }
    });

    this.translateService.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe(() => this.translateButtons());

    this.route.events.pipe(untilDestroyed(this)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.activateButtonByRoute(event.urlAfterRedirects);
      }
    });
  }

  register(appContainer: AppContainerOptions): void {
    this.appContainerOptions = appContainer;
    this.initButtons();
    appContainer.sideNavBar.buttons = this.buttons;
    this.appContainerOptions.rightDrawer.contents = [
      new RightDrawerContent({
        id: SideNavBarButtonsEnum.MESSAGES,
        componentType: MessageCenterComponent,
      }),
    ];
  }

  unregister(appContainer: AppContainerOptions): void {
    appContainer.sideNavBar.buttons = [];
    this.appContainerOptions = null;
  }

  initButtons(): void {
    this.translateButtons();
    this.syncButtonsArray();
    this.activateButtonByRoute(this.route.url);

    for (const btn of this.idToButtonMap.values()) {
      if (
        (btn instanceof LinkSideNavButton || btn instanceof ContentSideNavButton) &&
        btn.click &&
        !btn.click.observed
      ) {
        btn.click.subscribe(() => this.onLinkButtonClick(btn.id));
      }
    }
  }

  getButtons(): SideNavButton[] {
    return this.buttons;
  }

  private syncButtonsArray(): void {
    this.buttons = Array.from(this.idToButtonMap.values());
  }

  private translateButtons(): void {
    this.idToTranslationKey.forEach((key, id) => {
      const btn = this.idToButtonMap.get(id);
      if (btn && key) {
        btn.text = this.translateService.instant(key);
        btn.tooltip = btn.text;
      }
    });
  }

  private activateButtonByRoute(route: string): void {
    const segments = route.split('/');
    const last = segments[segments.length - 1];
    for (const [id, btn] of this.idToButtonMap.entries()) {
      btn.active = last === id;
    }
  }

  private async onLinkButtonClick(btnId: string): Promise<void> {
    if (!this.currentProject) return;

    const routeSegments = await this.getRouteSegments(btnId);
    if (routeSegments) {
      this.route.navigate(routeSegments);
      this.activateButtonByRoute(this.route.url);
    }
  }

  private async getRouteSegments(btnId: string): Promise<string[] | null> {
    if (!this.currentProject) return null;

    switch (btnId) {
      case SideNavBarButtonsEnum.DASHBOARD:
        return ['projects', this.currentProject.id, 'dashboard'];
      case SideNavBarButtonsEnum.DOCUMENTS:
        return ['projects', this.currentProject.id, 'documents'];
      default:
        return null;
    }
  }
}
